import {
  Card,
  CardContent,
  FormControlLabel,
  Stack,
  Switch,
  Typography,
} from '@mui/material';
import React, { useState } from 'react';
import { FormattedMessage, defineMessages, useIntl } from 'react-intl';

import ContentLoader from '@components/ContentLoader';
import Pill from '@components/Pill';

import { isDevelopment } from '@constants/env';

import { useUpdateNotificationConnection } from '@hooks/api/notification';

import type { Profile } from '@models/session';

import { formatName } from '@utils/format';
import { isDependant } from '@utils/role';

const messages = defineMessages({
  dependant: {
    id: 'notificationSettings.dependant',
    defaultMessage: 'DEPENDANT',
  },
  ownSettings: {
    id: 'notificationSettings.ownSettings',
    defaultMessage: 'Receive notifications about changes/updates to profile',
  },
  settings: {
    id: 'notificationSettings.settings',
    defaultMessage:
      "I want <b>{currentUser}</b> to {enabled, select, true{} other{not}} receive notifications about updates/changes to <b>{activeUser}'s</b> profile",
  },
  dependantSettings: {
    id: 'notificationSettings.dependantSettings',
    defaultMessage:
      'Dependants do not get notified about updates/changes to their profile',
  },
  error: {
    id: 'notificationSettings.error',
    defaultMessage:
      'There was an error while updating user notification preferences',
  },
  yes: {
    id: 'GLOBAL.yes',
    defaultMessage: 'Yes',
  },
  no: {
    id: 'GLOBAL.no',
    defaultMessage: 'No',
  },
});

interface UserNotificationConnectionProps {
  /**
   * User that the notification preferences are for
   */
  user: Profile;
  /**
   * User to enable/disable notifications for
   */
  connectingUser: Profile;
  /**
   * Whether the email notification setting is enabled or not
   */
  email_enabled: boolean;
  /**
   * Fetching notification preferences?
   */
  loading: boolean;
}

const UserNotificationConnection: React.FC<UserNotificationConnectionProps> = ({
  user,
  connectingUser,
  email_enabled,
  loading: baseLoading,
}) => {
  const { formatMessage } = useIntl();
  const [toggled, setToggled] = useState(false);

  const dependantUser = isDependant(connectingUser);

  const [updateNotificationConnection, updateNotificationConnectionLoading] =
    useUpdateNotificationConnection();

  const loading = updateNotificationConnectionLoading;
  const isEmailEnabled = loading ? toggled : email_enabled;

  const handleChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!user.user_id) {
      return;
    }
    try {
      const { checked } = event.target;
      setToggled(checked);

      await updateNotificationConnection({
        input: {
          user_id: user.user_id,
          email_enabled: checked,
          connected_user_id: connectingUser.user_id,
        },
      });
    } catch (e) {
      if (isDevelopment) {
        // eslint-disable-next-line no-console
        console.error(e);
      }
    }
  };

  return (
    <Card>
      <CardContent>
        <ContentLoader loading={baseLoading}>
          <Stack
            direction="row"
            spacing={2}
            alignItems={'center'}
            justifyContent={'space-between'}
          >
            <Typography>
              {dependantUser ? (
                formatMessage(messages.dependantSettings)
              ) : (
                <FormattedMessage
                  id={messages.settings.id}
                  values={{
                    b: (chunks) => <strong>{chunks}</strong>,
                    activeUser: formatName(user.first_name, user.last_name),
                    enabled: isEmailEnabled,
                    currentUser: formatName(
                      connectingUser.first_name,
                      connectingUser.last_name
                    ),
                  }}
                />
              )}
            </Typography>

            {dependantUser ? (
              <Pill
                color="primary"
                label={formatMessage(messages.dependant)}
                size="small"
              />
            ) : (
              <FormControlLabel
                control={
                  <Switch
                    checked={isEmailEnabled}
                    disabled={updateNotificationConnectionLoading}
                    onChange={handleChange}
                  />
                }
                label={formatMessage(
                  isEmailEnabled ? messages.yes : messages.no
                )}
              />
            )}
          </Stack>
        </ContentLoader>
      </CardContent>
    </Card>
  );
};

export default UserNotificationConnection;
